"use client";

import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import styles from "./revealOnScroll.module.scss";

export function RevealOnScroll({ children, offsetRem = 10, scrollDelayRem = 0, fullWidth = false }) {
	const containerRef = useRef(null);
	const [progress, setProgress] = useState(0);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;

		// Convert rem to pixels (assuming 1rem = 16px, but we'll get actual value)
		const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
		const scrollDelayPx = scrollDelayRem * rootFontSize;

		let rafId;
		let isActive = false;

		const update = () => {
			const rect = el.getBoundingClientRect();
			const vh = window.innerHeight;

			// Start animation when element enters viewport, complete when it reaches 25% from bottom (75% from top)
			// Apply scroll delay by offsetting the element's position (makes it start later)
			const start = vh; // Start when element enters viewport (at bottom)
			const end = vh * 0.75; // Complete at 25% from bottom (75% from top)
			// Offset rect.top by delay - items with delay appear "further down" so they start later
			const adjustedTop = rect.top + scrollDelayPx;
			let p = (start - adjustedTop) / (start - end);

			// clamp - once complete, stay at 1
			p = Math.max(0, Math.min(1, p));

			setProgress(p);

			if (isActive) {
				rafId = requestAnimationFrame(update);
			}
		};

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					if (!isActive) {
						isActive = true;
						// Set initial progress immediately
						const rect = el.getBoundingClientRect();
						const vh = window.innerHeight;
						const start = vh;
						const end = vh * 0.75;
						const adjustedTop = rect.top + scrollDelayPx;
						let initialP = (start - adjustedTop) / (start - end);
						initialP = Math.max(0, Math.min(1, initialP));
						setProgress(initialP);
					}
					update();
				} else {
					isActive = false;
					cancelAnimationFrame(rafId);
				}
			},
			{ threshold: [0, 0.25, 0.5, 0.75, 1], rootMargin: '100px' }
		);

		observer.observe(el);

		// Check if element is already visible on mount
		const rect = el.getBoundingClientRect();
		const vh = window.innerHeight;
		const start = vh;
		const end = vh * 0.75;
		const adjustedTop = rect.top + scrollDelayPx;
		let initialP = (start - adjustedTop) / (start - end);
		initialP = Math.max(0, Math.min(1, initialP));

		// If element is already past the completion point, set to fully visible
		if (adjustedTop <= end) {
			setProgress(1);
		} else if (adjustedTop < vh) {
			isActive = true;
			setProgress(initialP);
			update();
		}

		return () => {
			observer.disconnect();
			cancelAnimationFrame(rafId);
		};
	}, [scrollDelayRem]);

	// interpolate position: offsetRem → 0
	const translateY = (1 - progress) * offsetRem;

	return (
		<div
			ref={containerRef}
			className={clsx(styles.wrapper, fullWidth ? styles.full_width : '')}
			style={{
				transform: `translateY(${translateY}rem)`,
				opacity: progress,
			}}
		>
			{children}
		</div>
	);
}